import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";

type ShiftRow = {
  id: string;
  project_id: string;
  shift_date: string;
  clock_in: string;
  clock_out: string | null;
  total_hours: number | null;
  role: string;
  status: string;
};

type Props = {
  crewId: string;
};

export default function RecentShifts({ crewId }: Props) {
  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchShifts = async () => {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from("shifts")
        .select(
          "id, project_id, shift_date, clock_in, clock_out, total_hours, role, status",
        )
        .eq("crew_id", crewId)
        .order("shift_date", { ascending: false })
        .limit(20);
      if (cancelled) return;
      if (err) setError(err.message);
      else setShifts((data || []) as ShiftRow[]);
      setLoading(false);
    };
    fetchShifts();
    return () => {
      cancelled = true;
    };
  }, [crewId]);

  const visible = showAll ? shifts : shifts.slice(0, 6);
  const totalHours = shifts.reduce((s, x) => s + (Number(x.total_hours) || 0), 0);

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      <div className="px-5 pt-5 pb-4 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-bold text-ink-900">Recent Shifts</h3>
          <p className="text-[11px] text-ink-500 mt-0.5">
            {loading
              ? "Loading…"
              : shifts.length === 0
              ? "No shifts logged yet"
              : `Last ${visible.length} of ${shifts.length} · ${totalHours.toFixed(1)}h total`}
          </p>
        </div>
        {shifts.length > 6 && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-xs font-semibold text-brand-600 hover:text-brand-700 cursor-pointer whitespace-nowrap"
          >
            {showAll ? "Show less" : "View all"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-ink-400">Loading shifts…</div>
      ) : error ? (
        <div className="px-5 py-6 text-center text-sm text-rose-600 bg-rose-50 m-5 rounded-md">
          {error}
        </div>
      ) : shifts.length === 0 ? (
        <div className="py-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-cream-100 text-ink-400 flex items-center justify-center mb-2">
            <i className="ri-time-line text-xl"></i>
          </div>
          <div className="text-sm font-semibold text-ink-700">No shifts on record</div>
          <div className="text-xs text-ink-500 mt-1">
            This crew member hasn't clocked any shifts yet.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-ink-500 bg-cream-50 border-b border-ink-100">
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Date
                </th>
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Project
                </th>
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Clock In / Out
                </th>
                <th className="text-right font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Hours
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-ink-100 last:border-b-0 hover:bg-cream-50 transition-colors"
                >
                  <td className="px-5 py-3 text-sm text-ink-700 font-semibold whitespace-nowrap">
                    {s.shift_date || "—"}
                  </td>
                  <td className="px-5 py-3">
                    {s.project_id ? (
                      <Link
                        to={`/projects/${s.project_id}`}
                        className="font-mono text-[11px] font-bold text-brand-700 hover:underline cursor-pointer"
                      >
                        {s.project_id}
                      </Link>
                    ) : (
                      <span className="text-[11px] text-ink-400">—</span>
                    )}
                    <div className="text-[10px] text-ink-500">{s.role || "—"}</div>
                  </td>
                  <td className="px-5 py-3 text-xs text-ink-500 whitespace-nowrap tabular-nums">
                    {s.clock_in || "—"}{" "}
                    <span className="text-ink-300">→</span>{" "}
                    {s.clock_out || (
                      <span className="text-emerald-600 font-semibold">active</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-ink-900 tabular-nums">
                    {s.total_hours ? `${Number(s.total_hours).toFixed(1)}h` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}